import {IFinalEvaluatedArea, IMentalGameData, ISingleStickyDeadSymData, IStickyDeadSymData} from '../server/MentalData';
import {Mental} from "../Mental";
import {TimelineLite, TweenLite} from "gsap";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {DeadSymbolSticker} from "./DeadSymbolSticker";
import {DeadTransform} from "../winPresentations/deadFeature/DeadTransform";
import {MentalGameMode} from "../server/MentalGameMode";
import {ReelEnhancer} from "../server/ReelEnhancerData";
import {MentalSymbolStates} from "../reel/MentalSymbol";
import {MentalNormalSymbol} from "../reel/symbols/MentalNormalSymbol";
import {WinRatioType} from "../../framework/winPresentation/IWinRatios";
import {NoWinPresentation} from "../noWin/NoWinPresentation";
import {MentalSoundConfig} from "../MentalSoundConfig";
import {SoundManager} from "../sound/SoundManager";

export interface IGiveOutDeadMulti {
    symName: string;
    multiplier: number;
    reelId: number;
    symId: number;
    symSegment: number;
    symSize: number;
}

export class MentalDeadStickyMultiplier extends PIXI.Container implements StageLayer {
    // #148
    private static previousTint: number[][][] = [];
    public _stickySymbolsOnReel: DeadSymbolSticker[] = [];
    public readonly _symbolNames: string[] = ["M1", "M2", "M3", "M4", "M5"];
    public _stickySymbolCanBeUsed: number[] = [0, 0, 0, 0, 0];
    private _stickySymbolPositions: PIXI.Point[] = [];
    private _preserveSequence: number[] = [0, 1, 2, 3, 4];

    constructor() {
        super();
        this.initAnimation();
        this.onResize();
        this.visible = false;
    }

    // #148
    private static toggleDark(currentReelId: number, highestMultiplier: number, totalReels: number, fromReelStop: boolean, on: boolean): void {
        const rows: number = 3;
        let state: MentalSymbolStates = (highestMultiplier === 9999 ? MentalSymbolStates.SUPER_FS_ANTICIPATION : MentalSymbolStates.COOL_FS_ANTICIPATION);
        if (on) {
            Mental.background.playWinFlickerAnim(WinRatioType.LARGE, -1);

            if (!fromReelStop) {
                const tint: number = (state === MentalSymbolStates.SUPER_FS_ANTICIPATION ? 0x222222 : 0x444444);
                Mental.s2xWaysFeature.doTint(tint);
                Mental.enhancerReelPresentation.doTint(tint);

                Mental.xNudgePresentation.toggleTintForSpecialMultipliers(tint);
            }

            this.previousTint = [];
        } else {
            Mental.background.stopWinFlicker();

            if (!fromReelStop) {
                Mental.s2xWaysFeature.doTint(0xffffff);
                Mental.enhancerReelPresentation.doTint(0xffffff);

                Mental.xNudgePresentation.toggleTintForSpecialMultipliers(0xffffff);
            }
        }

        for (let reel: number = 0; reel < totalReels; reel++) {
            if (on) {
                this.previousTint[reel] = [];
            }

            for (let row: number = 0; row < rows; row++) {
                if (on) {
                    this.previousTint[reel][row] = [];
                }

                let visibleSymbol: MentalNormalSymbol = (fromReelStop ? (Mental.reelArea.getReel(reel).model.getStopSymbolAt(row).positionable) as MentalNormalSymbol : (Mental.reelArea.getReel(reel).model.getReelSymbolAt(row).positionable) as MentalNormalSymbol);
                if (visibleSymbol._splitSymbols.length) {
                    for (let symSegment: number = 0; symSegment < visibleSymbol._splitSymbols.length; symSegment++) {
                        if ((reel !== currentReelId) || (reel === currentReelId && visibleSymbol._splitSymbols[symSegment]._symbolMultiplierValue !== highestMultiplier)) {
                            if (on) {
                                this.previousTint[reel][row][symSegment] = visibleSymbol._splitSymbols[symSegment].getTint();
                                visibleSymbol._splitSymbols[symSegment].setState(state);
                            } else {
                                visibleSymbol._splitSymbols[symSegment].applyTint(this.previousTint[reel][row][symSegment]);
                            }
                        }
                    }
                } else {
                    const symSegment: number = 0;
                    if ((reel !== currentReelId) || (reel === currentReelId && visibleSymbol._symbolMultiplierValue !== highestMultiplier)) {
                        if (on) {
                            this.previousTint[reel][row][symSegment] = visibleSymbol.getTint();
                            visibleSymbol.setState(state);
                        } else {
                            visibleSymbol.applyTint(this.previousTint[reel][row][symSegment]);
                        }
                    }
                }
            }
        }
    }

    public onResize() {
        this.scale.set(1.2);
        this.position.set(0, 0);

        if (NolimitApplication.isLandscape) {
            this.children.forEach((dObj: PIXI.DisplayObject, index: number) => {
                const syms = <DeadSymbolSticker>dObj
                syms.position.set(640, 110 + (index * 70));
                syms.repositionSubChild(NolimitApplication.isLandscape);
            });
        } else {

            this.alignStickers();
            this.position.y = 85;

            this.children.forEach((dObj: PIXI.DisplayObject, index: number) => {
                const syms = <DeadSymbolSticker>dObj
                syms.position.set(280 + (index * 100), -50);
                syms.repositionSubChild(NolimitApplication.isLandscape);
            });
        }
        this.rePositionAsPerSequence(true);

    }

    public alignStickers(): void {
        if (!(NolimitApplication.isLandscape)) {
            let visibleStickerCount: number = 0;

            this.children.forEach((sticker: PIXI.DisplayObject) => {
                if (sticker.visible) {
                    visibleStickerCount++;
                }
            });

            if (visibleStickerCount === 5) {
                this.position.x = -160; //-210;
            } else if (visibleStickerCount === 4) {
                this.position.x = -40; //-156;
            } else if (visibleStickerCount === 3) {
                this.position.x = 80; //-96;
            } else if (visibleStickerCount === 2) {
                this.position.x = 200; //-35;
            } else {
                this.position.x = 320; //24;
            }
        }
    }

    public showSymbolStickers(): void {
        this.visible = true;
    }

    public hideSymbolStickers(): void {
        this.resetStickySymbols();
        this.visible = false;
    }

    public resetStickySymbols(): void {
        for (let i: number = 0; i < this._stickySymbolsOnReel.length; i++) {
            const stickySymbol: DeadSymbolSticker = this._stickySymbolsOnReel[i];
            stickySymbol.updateMultiplierTo(0);
            stickySymbol.updateSymCount(0);
            stickySymbol.position = this._stickySymbolPositions[i];
            stickySymbol.dimDown();
        }
    }

    public geStickySymbolByName(name: string): DeadSymbolSticker {
        for (let i = 0; i < this._stickySymbolsOnReel.length; i++) {
            if (name === this._stickySymbolsOnReel[i].name) {
                return this._stickySymbolsOnReel[i];
            }
        }
        return this._stickySymbolsOnReel[0];
    }

    public reArrangeStickySymbols(stickyData: IStickyDeadSymData): void {
        this._preserveSequence = [];
        const stickySym: string[] = Object.keys(stickyData);
        const allMultiValue: number[] = [];
        for (let i = 0; i < stickySym.length; i++) {
            allMultiValue.push(stickyData[stickySym[i]].multiplier);
        }

        allMultiValue.sort((a: number, b: number) => b - a);

        const filterForSameMulti: number[] = [];
        for (let i: number = 0; i < allMultiValue.length; i++) {
            for (let j: number = 0; j < this._stickySymbolsOnReel.length; j++) {
                if (!filterForSameMulti.includes(j) && this._stickySymbolsOnReel[j].multiplierValue === allMultiValue[i]) {
                    filterForSameMulti.push(j);
                    this._preserveSequence.push(j);
                    break;
                }
            }
        }
        if (this._preserveSequence.length !== 5) {
            for (let i: number = 0; i < 5; i++) {
                if (!this._preserveSequence.includes(i)) {
                    this._preserveSequence.push(i);
                }
            }
        }
        this.rePositionAsPerSequence(false);
    }

    public showIconStickers(stickyData: IStickyDeadSymData) {
        let counter: number = 0;
        const stickySym: string[] = Object.keys(stickyData);
        for (let i = 0; i < stickySym.length; i++) {
            const stickySymbol: DeadSymbolSticker = this.geStickySymbolByName(stickySym[i]);
            if (!stickySymbol._isDim) {
                counter++;
            }
        }

        for (let i = 0; i < stickySym.length; i++) {
            const stickySymbol: DeadSymbolSticker = this.geStickySymbolByName(stickySym[i]);
            if (stickySymbol._isDim) {
                TweenLite.to(stickySymbol.position, {
                    y: NolimitApplication.isLandscape ? 110 + (counter * 70) : -50,
                    x: NolimitApplication.isLandscape ? this._stickySymbolPositions[counter].x : 280 + (counter * 100),
                    duration: 0.001
                }).eventCallback("onComplete", () => {
                    stickySymbol.onlyShowIcon();
                });
                counter++;
            }
        }
        this.alignStickers();
    }

    public playStickyAnim(stickyData: IStickyDeadSymData): TimelineLite {
        const data: string[] = Object.keys(stickyData),
            tl: TimelineLite = new TimelineLite();

        for (let i = 0; i < data.length; i++) {
            const values: ISingleStickyDeadSymData = stickyData[data[i]];
            const stickySymbol: DeadSymbolSticker = this._stickySymbolsOnReel[this._symbolNames.indexOf(data[i])];
            if (!values.count) {
                stickySymbol.dimDown();
            } else {
                stickySymbol._isDim && tl.add(stickySymbol.unDim(), 0);
            }
            tl.add(() => {
                stickySymbol.updateMultiplierTo(values.multiplier);
                stickySymbol.updateSymCount(values.count);
                this._stickySymbolCanBeUsed[this._symbolNames.indexOf(data[i])] = values.count;
            }, 0)

            Logger.logDev("STICKY_SYMBOL_DATA", stickyData[data[i]]);
        }
        tl.add(() => {
            this.reArrangeStickySymbols(stickyData)
        });
        return tl;
    }

    public playGiveOutAnimAfterXWaysReveal(stickyData: IStickyDeadSymData): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (Object.keys(stickyData).length > 0) {
            tl.add(this.distributeMultiplierOnXWaysReveal(stickyData), 0);

            tl.add(() => {
                this.reArrangeInCaseOfCoolFs();
            });
        }
        return tl;
    }

    public playGiveOutAnimOnSpecialXSplit(stickyData: IStickyDeadSymData, reelId: number, symId: number, innerSymId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
            paused: true
        });

        if (Object.keys(stickyData).length > 0) {
            tl.add(this.distributeMultiplierOnSpecialXSplit(stickyData, reelId, symId, innerSymId), 0);

            tl.add(() => {
                this.reArrangeInCaseOfCoolFs();
            });
        }
        return tl.play(0);
    }

    public playGiveOutAnimOnReelStop(stickyData: IStickyDeadSymData, reelId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (Object.keys(stickyData).length > 0) {
            tl.add(this.distributeMultiplierOnReelStop(stickyData, reelId), 0);

            tl.add(() => {
                this.reArrangeInCaseOfCoolFs();
            });
        }

        return tl;
    }

    public playGiveOutAnim(stickyData: IStickyDeadSymData, reelId: number, symId: number, innerSymId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (Object.keys(stickyData).length > 0) {
            tl.add(this.distributeMultiplier(stickyData, reelId, symId, innerSymId), 0);

            tl.add(() => {
                this.reArrangeInCaseOfCoolFs();
            });
        }
        return tl;
    }

    private initAnimation(): void {
        for (let i: number = 0; i < 5; i++) {
            const deadSticker: DeadSymbolSticker = new DeadSymbolSticker(this._symbolNames[i], i);
            this._stickySymbolPositions.push(deadSticker.position);
            deadSticker.dimDown();
            deadSticker.name = this._symbolNames[i];
            this._stickySymbolsOnReel.push(deadSticker);
            this.addChild(deadSticker);
        }
    }

    private reArrangeInCaseOfCoolFs(): void {
        if (Mental.model.gameData.mode === MentalGameMode.COOL_FREESPIN) {
            let counter: number = 0;
            const initPosY: number = 110;
            for (let i: number = 0; i < this._preserveSequence.length; i++) {
                const stickySymbol: DeadSymbolSticker = this._stickySymbolsOnReel[this._preserveSequence[i]];
                if (!stickySymbol._isDim) {
                    TweenLite.to(stickySymbol.position, {
                        y: NolimitApplication.isLandscape ? initPosY + (counter * 70) : -50,
                        x: NolimitApplication.isLandscape ? this._stickySymbolPositions[counter].x : 280 + (counter * 100),
                        duration: 0.1
                    });
                    counter++;
                    stickySymbol.repositionSubChild(NolimitApplication.isLandscape);
                }
            }

            for (let i: number = 0; i < this._preserveSequence.length; i++) {
                const stickySymbol: DeadSymbolSticker = this._stickySymbolsOnReel[this._preserveSequence[i]];
                if (stickySymbol._isDim) {
                    TweenLite.to(stickySymbol.position, {
                        y: NolimitApplication.isLandscape ? initPosY + (counter * 70) : -50,
                        x: NolimitApplication.isLandscape ? this._stickySymbolPositions[counter].x : 280 + (counter * 100),
                        duration: 0.1
                    });
                    counter++;
                    stickySymbol.repositionSubChild(NolimitApplication.isLandscape);
                }
            }
        }
        this.alignStickers();
    }

    private rePositionAsPerSequence(onResize: boolean = false): void {
        let counter: number = 0;
        const initPosY: number = 110;
        for (let i: number = 0; i < this._stickySymbolsOnReel.length; i++) {
            const stickySymbol: DeadSymbolSticker = this._stickySymbolsOnReel[this._preserveSequence[i]];
            TweenLite.to(stickySymbol.position, {
                y: NolimitApplication.isLandscape ? initPosY + (counter * 70) : -50,
                x: NolimitApplication.isLandscape ? this._stickySymbolPositions[counter].x : 280 + (counter * 100),
                duration: onResize ? 0.001 : 0.2
            });
            counter++;

            stickySymbol.repositionSubChild(NolimitApplication.isLandscape);
        }
        this.alignStickers();
    }

    private distributeMultiplierOnXWaysReveal(stickyData: IStickyDeadSymData): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            stickySyms: string[] = Object.keys(stickyData);

        for (let i: number = 0; i < stickySyms.length; i++) {
            if (stickyData[stickySyms[i]].used > 0 && stickyData[stickySyms[i]].used > this._stickySymbolCanBeUsed[this._symbolNames.indexOf(stickySyms[i])]) {
                tl.add(this.findSymOnReelAreaOnXWaysReveal(stickySyms[i]), 0);
            }
        }

        return tl;
    }

    private findSymOnReelAreaOnXWaysReveal(symName: string): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            data: IMentalGameData = Mental.model.gameData,
            finalEvalArea: IFinalEvaluatedArea[][][] = data.finalEvaluatedArea;
        let soundTime: number = 0,
            winSymFound: boolean = false,
            isWinSym: boolean = false;

        [1, 3].forEach((reelId: number) => {
            for (let symId: number = 0; symId < finalEvalArea[reelId].length; symId++) {
                const symSize: number = data.currentSymbolSize[reelId][symId];
                for (let innerSymId: number = 0; innerSymId < finalEvalArea[reelId][symId].length; innerSymId++) {
                    const symData: IFinalEvaluatedArea = finalEvalArea[reelId][symId][innerSymId];

                    if (symData && symName === symData.name && !(symData.wasDead) && symData.multiplierOccurrence > 0) {
                        let prevSize: number = 0,
                            l: number = 0;
                        if (innerSymId > 0) {
                            for (let i: number = 0; i < innerSymId; i++) {
                                prevSize += finalEvalArea[reelId][symId][i].size;
                            }
                        }
                        for (let symSegment: number = prevSize; symSegment < prevSize + finalEvalArea[reelId][symId][innerSymId].size; symSegment++) {
                            if (l >= symData.multiplierOccurrence) {
                                break;
                            }
                            if (!Mental.model.gameData.deadMultiplierPlacedPositions[reelId][symId][symSegment]) {
                                l++;
                                Mental.model.gameData.deadMultiplierPlacedPositions[reelId][symId][symSegment] = true;
                                this._stickySymbolCanBeUsed[this._symbolNames.indexOf(symData.name)] += 1;
                                let deadSymTransform: DeadTransform = new DeadTransform({
                                    "reelId": reelId,
                                    "symId": symId,
                                    "size": symSize,
                                    "segment": symSegment
                                });
                                deadSymTransform.name = "deadTransform_" + reelId + "_" + symId + "_" + symSegment;
                                tl.add(() => {
                                    Mental.deadFeaturePresentation.addChild(deadSymTransform);
                                    Mental.model.gameData.deadMultiplierPlacedValue[reelId][symId][symSegment] = symData.multiplier;

                                    deadSymTransform.setData(reelId, symId, symSegment);
                                }, 0);
                                tl.add(deadSymTransform.playFlyBackAnim(symName, symData.multiplier, reelId, symId, symSegment), 0);

                                if (symData.multiplier > 999) {
                                    if (soundTime === 0) {
                                        soundTime = tl.totalDuration();
                                    }
                                    isWinSym = this.tryTurningDarkAllReels(tl, reelId, symData.multiplier, symId, symSegment);
                                    if (!winSymFound) {
                                        winSymFound = isWinSym;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        });

        // #374
        this.playSoundForSpecialMultiplier(soundTime, tl, winSymFound);

        this.alignStickers();
        return tl;
    }

    private distributeMultiplierOnSpecialXSplit(stickyData: IStickyDeadSymData, reelId: number, symId: number, innerSymId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
                paused: true
            }),
            stickySyms: string[] = Object.keys(stickyData);

        for (let i: number = 0; i < stickySyms.length; i++) {
            if (stickyData[stickySyms[i]].used > 0 && stickyData[stickySyms[i]].used > this._stickySymbolCanBeUsed[this._symbolNames.indexOf(stickySyms[i])]) {
                // #409
                tl.add(() => {
                    this.findSymOnReelAreaOnSpecialXSplit(stickySyms[i], reelId, symId, innerSymId);
                }, 0);
                tl.to(this, {
                    duration: 0.7
                });
            }
        }

        this.alignStickers();

        return tl.play(0);
    }

    private findSymOnReelAreaOnSpecialXSplit(symName: string, reelId: number, symId: number, innerSymId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite({paused: true}),
            data: IMentalGameData = Mental.model.gameData,
            finalEvalArea: IFinalEvaluatedArea[][][] = data.finalEvaluatedArea;

        let symData: IFinalEvaluatedArea;

        if (data.scatter2Position[reelId] === symId && data.firstS2FeatureTriggered.length > 1) {
            symData = finalEvalArea[reelId][symId][innerSymId];
        } else {
            symData = finalEvalArea[reelId][symId][0];
        }

        let canDistribute: boolean = true;

        if (symId == data.scatter2Position[reelId]) {
            const featureTriggered: string[] = reelId === 1 ? data.firstS2FeatureTriggered : data.secondS2FeatureTriggered;

            if (featureTriggered[innerSymId] === ReelEnhancer.XWAYS) {
                canDistribute = false;
            }
        }

        if (canDistribute && symData && symName === symData.name && !(symData.wasDead) && symData.multiplierOccurrence > 0) {
            let symSize: number = symData.size;
            const tempData: IGiveOutDeadMulti[] = [];

            let prevSize: number = 0,
                l: number = 0,
                symSegment: number = innerSymId;
            if (innerSymId > 0) {
                if (data.scatter2Position[reelId] === symId && data.firstS2FeatureTriggered.length > 1) {
                    for (let i: number = 0; i < innerSymId; i++) {
                        prevSize += finalEvalArea[reelId][symId][i].size;
                    }

                    symSegment = prevSize;
                } else {
                    symSegment = innerSymId;
                }
            }

            for (; symSegment < symSize; symSegment++) {
                if (l >= symData.multiplierOccurrence) {
                    break;
                }

                if (!Mental.model.gameData.deadMultiplierPlacedPositions[reelId][symId][symSegment] &&
                    (Mental.model.gameData.stickyDeadSymCounter[symData.name].used > this._stickySymbolCanBeUsed[this._symbolNames.indexOf(symData.name)])) {
                    l++;

                    Mental.model.gameData.deadMultiplierPlacedPositions[reelId][symId][symSegment] = true;
                    this._stickySymbolCanBeUsed[this._symbolNames.indexOf(symData.name)] += 1;

                    Mental.model.gameData.deadMultiplierPlacedValue[reelId][symId][symSegment] = symData.multiplier;

                    tempData.push({
                        symName: symName,
                        multiplier: symData.multiplier,
                        reelId: reelId,
                        symId: symId,
                        symSegment: symSegment,
                        symSize: symSize
                    });
                }
            }

            let soundTime: number = 0,
                winSymFound: boolean = false,
                isWinSym: boolean = false;

            for (let i: number = 0; i < tempData.length; i++) {
                let deadSymTransform: DeadTransform;
                deadSymTransform = new DeadTransform({
                    "reelId": tempData[i].reelId,
                    "symId": tempData[i].symId,
                    "size": Mental.model.gameData.currentSymbolSize[reelId][symId],
                    "segment": tempData[i].symSegment
                });

                deadSymTransform.name = "deadTransform_" + tempData[i].reelId + "_" + tempData[i].symId + "_" + tempData[i].symSegment;
                Mental.deadFeaturePresentation.addChild(deadSymTransform);

                tl.add(() => deadSymTransform.setData(tempData[i].reelId, tempData[i].symId, tempData[i].symSegment), 0);
                tl.add(deadSymTransform.playFlyBackAnim(
                    tempData[i].symName,
                    tempData[i].multiplier,
                    tempData[i].reelId,
                    tempData[i].symId,
                    tempData[i].symSegment), 0);

                if (symData.multiplier > 999) {
                    if (soundTime === 0) {
                        soundTime = tl.totalDuration();
                    }
                    isWinSym = this.tryTurningDarkAllReels(tl, reelId, symData.multiplier, symId, tempData[i].symSegment);
                    if (!winSymFound) {
                        winSymFound = isWinSym;
                    }
                }
            }

            // #374
            this.playSoundForSpecialMultiplier(soundTime, tl, winSymFound);
        }

        this.alignStickers();
        return tl.play(0);
    }

    private distributeMultiplierOnReelStop(stickyData: IStickyDeadSymData, reelId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            stickySyms: string[] = Object.keys(stickyData);

        for (let i: number = 0; i < stickySyms.length; i++) {
            if (stickyData[stickySyms[i]].used > 0 && stickyData[stickySyms[i]].used > this._stickySymbolCanBeUsed[this._symbolNames.indexOf(stickySyms[i])]) {
                tl.add(this.findSymOnReelAreaOnReelStop(stickySyms[i], reelId), 0);
            }
        }

        return tl;
    }

    private findSymOnReelAreaOnReelStop(symName: string, reelId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            data: IMentalGameData = Mental.model.gameData,
            finalEvalArea: IFinalEvaluatedArea[][][] = data.finalEvaluatedArea;
        let highestMultiplier: number = -1;

        for (let symId: number = 0; symId < finalEvalArea[reelId].length; symId++) {
            // skip for S2 and enhancer.
            if (((symId === data.scatter2Position[reelId] || symId === 2) && reelId === 1) || ((symId === data.scatter2Position[reelId] || symId === 2) && reelId === 3)) {
                continue;
            }

            const symSize: number = data.currentSymbolSize[reelId][symId];

            for (let innerSymId: number = 0; innerSymId < symSize; innerSymId++) {
                const symData: IFinalEvaluatedArea = finalEvalArea[reelId][symId][innerSymId];

                let index: number = innerSymId;

                if (symData && symName === symData.name && !(symData.wasDead) && symData.multiplierOccurrence > 0) {
                    let soundTime: number = 0,
                        winSymFound: boolean = false;

                    for (let l: number = 0; l < symSize; l++) {
                        if (l >= symData.multiplierOccurrence) {
                            break;
                        }
                        const symSegment: number = index + l;
                        let deadSymTransform: DeadTransform = new DeadTransform({
                            "reelId": reelId,
                            "symId": symId,
                            "size": symSize,
                            "segment": symSegment
                        });
                        deadSymTransform.name = "deadTransform_" + reelId + "_" + symId + "_" + symSegment;
                        this._stickySymbolCanBeUsed[this._symbolNames.indexOf(symData.name)] += 1;
                        tl.add(() => {
                            Mental.deadFeaturePresentation.addChild(deadSymTransform);
                            Mental.model.gameData.deadMultiplierPlacedValue[reelId][symId][symSegment] = symData.multiplier;
                            Mental.model.gameData.deadMultiplierPlacedPositions[reelId][symId][symSegment] = true;

                            deadSymTransform.setData(reelId, symId, symSegment);
                        }, 0);
                        tl.add(deadSymTransform.playFlyBackAnim(symName, symData.multiplier, reelId, symId, symSegment, true), 0);

                        // #148
                        highestMultiplier = symData.multiplier;
                        if (highestMultiplier > 999) {
                            tl.add(() => MentalDeadStickyMultiplier.toggleDark(reelId, highestMultiplier, (reelId + 1), true, true), tl.totalDuration());

                            const isWinSym: boolean = (reelId < 2 ? true : NoWinPresentation.getWinNoWinSymStatus(reelId, symId, symSegment));
                            let time: number = 1;
                            if (highestMultiplier === 9999 && isWinSym) {
                                time = 2;
                            } else if (highestMultiplier === 1000 && !isWinSym) {
                                time = 0.5;
                            }

                            if (soundTime === 0) {
                                soundTime = tl.totalDuration();
                            }
                            if (!winSymFound) {
                                winSymFound = isWinSym;
                            }

                            tl.add(() => MentalDeadStickyMultiplier.toggleDark(reelId, highestMultiplier, (reelId + 1), true, false), (tl.totalDuration() + time));
                        }
                    }

                    // #374
                    this.playSoundForSpecialMultiplier(soundTime, tl, winSymFound);
                }
            }
        }
        this.alignStickers();
        return tl;
    }

    private distributeMultiplier(stickyData: IStickyDeadSymData, reelId: number, symId: number, innerSymId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            stickySyms: string[] = Object.keys(stickyData);

        for (let i: number = 0; i < stickySyms.length; i++) {
            if (stickyData[stickySyms[i]].used > 0 && stickyData[stickySyms[i]].used > this._stickySymbolCanBeUsed[this._symbolNames.indexOf(stickySyms[i])]) {
                tl.add(this.findSymOnReelArea(stickySyms[i], reelId, symId, innerSymId), 0);
            }
        }

        this.alignStickers();

        return tl;
    }

    private findSymOnReelArea(symName: string, reelId: number, symId: number, innerSymId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            data: IMentalGameData = Mental.model.gameData,
            finalEvalArea: IFinalEvaluatedArea[][][] = data.finalEvaluatedArea;
        let symData: IFinalEvaluatedArea;

        // wrote this to access the correct symbol in final evaluated area that's it!!
        if ((reelId === 1) && data.scatter2Position[reelId] === symId) {
            let innerSymIdFallsUnder: number = 0;
            let counter: number = 0;
            for (let i = 0; i < finalEvalArea[reelId][symId].length; i++) {
                if (counter >= innerSymId && (innerSymId === 0 || !finalEvalArea[reelId][symId][i].wasDead)) { // to ix #385
                    innerSymIdFallsUnder = i;
                    break;
                }
                counter += finalEvalArea[reelId][symId][i].size;
            }
            symData = finalEvalArea[reelId][symId][innerSymIdFallsUnder];
        } else if (reelId === 3 && data.scatter2Position[reelId] === symId) {
            // to fix for reel id 3, all s2 feature comes in different object in finalEvaluatedArea.
            symData = data.finalEvaluatedArea[reelId][symId][innerSymId];
        } else if (finalEvalArea[reelId][symId].length <= innerSymId && reelId === 1) { //  when reverse xSplit runs from reel  3 to 1;
            symData = finalEvalArea[reelId][symId][finalEvalArea[reelId][symId].length - 1];
        } else {
            symData = finalEvalArea[reelId][symId][0];
        }

        let index: number = 0;

        if ((reelId == 1 || reelId === 3) && symId == data.scatter2Position[reelId]) {
            const featureTriggered: string[] = reelId === 1 ? data.firstS2FeatureTriggered : data.secondS2FeatureTriggered;

            for (let i: number = 0; i < innerSymId; i++) {
                switch (featureTriggered[i]) {
                    case ReelEnhancer.XWAYS:
                        index += 3;
                        break;
                    case ReelEnhancer.XSPLIT:
                        index += 2;
                        break;
                    default:
                        index += 1;
                        break;
                }
            }
        } else {
            index = innerSymId;
        }

        if (symData && symName === symData.name && !(symData.wasDead) && symData.multiplierOccurrence > 0) {
            let symSize: number = symData.size,
                soundTime: number = 0,
                winSymFound: boolean = false,
                isWinSym: boolean = false;
            for (let l: number = 0; l < symSize; l++) {
                if (l >= symData.multiplierOccurrence) {
                    break;
                }
                const symSegment: number = index;
                if (!Mental.model.gameData.deadMultiplierPlacedPositions[reelId][symId][symSegment] &&
                    (Mental.model.gameData.stickyDeadSymCounter[symData.name].used > this._stickySymbolCanBeUsed[this._symbolNames.indexOf(symData.name)])) {
                    Mental.model.gameData.deadMultiplierPlacedPositions[reelId][symId][symSegment] = true;
                    this._stickySymbolCanBeUsed[this._symbolNames.indexOf(symData.name)] += 1;

                    let deadSymTransform: DeadTransform = new DeadTransform({
                        "reelId": reelId,
                        "symId": symId,
                        "size": Mental.model.gameData.currentSymbolSize[reelId][symId],
                        "segment": symSegment
                    });

                    deadSymTransform.name = "deadTransform_" + reelId + "_" + symId + "_" + symSegment;
                    tl.add(() => {
                        Mental.deadFeaturePresentation.addChild(deadSymTransform);
                        Mental.model.gameData.deadMultiplierPlacedValue[reelId][symId][symSegment] = symData.multiplier;

                        deadSymTransform.setData(reelId, symId, symSegment);
                    }, 0);
                    tl.add(deadSymTransform.playFlyBackAnim(symName, symData.multiplier, reelId, symId, symSegment), 0);

                    if (symData.multiplier > 999) {
                        if (soundTime === 0) {
                            soundTime = tl.totalDuration();
                        }
                        isWinSym = this.tryTurningDarkAllReels(tl, reelId, symData.multiplier, symId, symSegment);
                        if (!winSymFound) {
                            winSymFound = isWinSym;
                        }
                    }
                }
            }

            // #374
            this.playSoundForSpecialMultiplier(soundTime, tl, winSymFound);
        }
        this.alignStickers();
        return tl;
    }

    // #148
    private tryTurningDarkAllReels(tl: TimelineLite, reelId: number, highestMultiplier: number, symId: number, symSegment: number): boolean {
        tl.add(() => MentalDeadStickyMultiplier.toggleDark(reelId, highestMultiplier, 5, false, true), tl.totalDuration());

        const isWinSym: boolean = NoWinPresentation.getWinNoWinSymStatus(reelId, symId, symSegment);
        let time: number = 1;
        if (highestMultiplier === 9999 && isWinSym) {
            time = 2;
        } else if (highestMultiplier === 1000 && !isWinSym) {
            time = 0.5;
        }

        tl.add(() => MentalDeadStickyMultiplier.toggleDark(reelId, highestMultiplier, 5, false, false), (tl.totalDuration() + time));

        return isWinSym;
    }

    // #374
    private playSoundForSpecialMultiplier(soundTime: number, tl: TimelineLite, winSymFound: boolean): void {
        if (soundTime !== 0) {
            tl.add(() => {
                const soundName: string = (winSymFound ? MentalSoundConfig.MULT_LAND : MentalSoundConfig.MULT_DEAD);

                SoundManager.playSoundEffect(soundName);
            }, soundTime);
        }
    }
}